import React, { useState, useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line yola/import/no-extraneous-dependencies
import {
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  offset,
  autoUpdate,
  arrow,
  safePolygon,
} from '@floating-ui/react';
import PropTypes from 'prop-types';
import {
  PLACEMENTS,
  APPEARANCES,
  MODES,
  MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS,
  FLOATING_OFFSET,
} from './constants';
import PopoverContext from './context';
import { PopoverContent, PopoverTrigger } from './components';

const Popover = ({
  children,
  hotkey = '',
  placement = PLACEMENTS.topCenter,
  title = '',
  width = null,
  onHover = Function.prototype,
  disabled = false,
  appearance = APPEARANCES.light,
  mode = MODES.tooltip,
  showOnMount = false,
  imgSrc = '',
  isOpen = null,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isVisible,
    placement: MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS[placement],
    middleware: [
      offset(FLOATING_OFFSET),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    onOpenChange: (isShowed, event) => {
      if (mode === MODES.popover && !isShowed && event.target.closest('.ws-ui-popover-content')) {
        return;
      }
      setIsVisible(isShowed);
    },
  });

  const hover = useHover(context, {
    enabled: mode === MODES.tooltip && !disabled,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: mode === MODES.tooltip && !disabled });
  const click = useClick(context, {
    enabled: mode === MODES.popover && !disabled && isOpen === null,
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);

  const showPopover = () => {
    if (disabled) {
      return;
    }

    setIsVisible(true);
  };

  const hidePopover = useCallback(() => {
    if (disabled || !isVisible) {
      return;
    }

    setIsVisible(false);
  }, [disabled, isVisible]);

  const onMouseEnter = () => {
    if (disabled) {
      return;
    }

    onHover();
  };

  useEffect(() => {
    if (!showOnMount) return () => {};
    showPopover();

    document.addEventListener('click', hidePopover, { once: true });

    return () => document.removeEventListener('click', hidePopover);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    if (!isVisible) return () => {};

    document.addEventListener('scroll', hidePopover, true);

    return () => document.removeEventListener('scroll', hidePopover, true);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [isVisible, hidePopover]);

  return (
    <PopoverContext.Provider
      value={{
        isVisible: isOpen === null ? isVisible : isOpen,
        title,
        width,
        hotkey,
        appearance,
        imgSrc,
        refs,
        floatingStyles,
        arrowRef,
        context,
        getFloatingProps,
      }}
    >
      <div
        className="ws-ui-popover"
        ref={refs.setReference}
        {...getReferenceProps({ onMouseEnter })}
      >
        {children}
      </div>
    </PopoverContext.Provider>
  );
};

Popover.propTypes = {
  placement: PropTypes.oneOf(Object.values(PLACEMENTS)),
  children: PropTypes.node.isRequired,
  title: PropTypes.node,
  hotkey: PropTypes.string,
  width: PropTypes.number,
  onHover: PropTypes.func,
  disabled: PropTypes.bool,
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  mode: PropTypes.oneOf(Object.values(MODES)),
  showOnMount: PropTypes.bool,
  imgSrc: PropTypes.string,
  isOpen: PropTypes.bool,
};

export { Popover, PopoverTrigger, PopoverContent };
